/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  accessAPI,
  Titulo,
  Boton,
  Sidebar,
  CampoInformacion,
  tituloBase,
  TablaFiltrable
} from "../../../shared";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Menu } from "primereact/menu";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

const SEARCH_FIELDS = ["nombre", "resumen"];

// Gestión de los documentos normativos que consulta el asistente Juno. Al
// subir un documento, la API guarda el original y extrae el texto en
// background: mientras tanto el documento figura "Procesando".
export default function JunoConfig() {
  const [documentos, setDocumentos] = useState(null);
  const [subiendoArchivo, setSubiendoArchivo] = useState(false);
  // Documento sobre el que se abrió el menú kebab; el sidebar de detalles y
  // el eliminar actúan sobre él.
  const [documentoSeleccionadoId, setDocumentoSeleccionadoId] = useState(null);
  const [sidebarDetalles, setSidebarDetalles] = useState(false);
  const toast = useRef(null);
  const inputArchivoRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    document.title = "Juno config";
    cargarDocumentos();
    return () => (document.title = tituloBase());
  }, []);

  // Mientras haya documentos procesándose, recarga la lista cada 5 segundos
  // para reflejar cuando terminan.
  useEffect(() => {
    if (!documentos?.some((d) => d.estado === "PROCESANDO")) return;
    const intervalo = setInterval(cargarDocumentos, 5000);
    return () => clearInterval(intervalo);
  }, [documentos]);

  function cargarDocumentos() {
    accessAPI(
      "GET",
      "admin/juno/documentos",
      null,
      (respuesta) => {
        setDocumentos(respuesta);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar los documentos",
          sticky: true
        });
      }
    );
  }

  function handleUploadArchivo(event) {
    const file = event.target.files?.[0];
    // Resetear el input permite volver a elegir el mismo archivo
    event.target.value = "";
    if (!file) return;

    setSubiendoArchivo(true);
    accessAPI(
      "POST",
      "admin/juno/documentos",
      null,
      (respuesta) => {
        setSubiendoArchivo(false);
        setDocumentos((prev) => [respuesta, ...(prev || [])]);
        toast.current?.show({
          severity: "success",
          summary: "Subido",
          detail: "Archivo subido, extrayendo el contenido..."
        });
      },
      (respuesta) => {
        setSubiendoArchivo(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al subir el archivo",
          sticky: true
        });
      },
      file
    );
  }

  function reprocesarDocumento(documento) {
    accessAPI(
      "POST",
      `admin/juno/documentos/${documento.id}/reprocesar`,
      null,
      (respuesta) => {
        setDocumentos((prev) =>
          prev.map((d) => (d.id === respuesta.id ? respuesta : d))
        );
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al reprocesar el documento",
          sticky: true
        });
      }
    );
  }

  function eliminarDocumento(documento) {
    confirmDialog({
      message: `¿Eliminar "${documento.nombre}"?`,
      header: "Confirmar eliminación",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Eliminar",
      rejectLabel: "Cancelar",
      acceptClassName: "p-button-danger",
      accept: () => {
        accessAPI(
          "DELETE",
          `admin/juno/documentos/${documento.id}`,
          null,
          (respuesta) => {
            setSidebarDetalles(false);
            setDocumentoSeleccionadoId(null);
            setDocumentos((prev) => prev.filter((d) => d.id !== respuesta.id));
            toast.current?.show({
              severity: "success",
              summary: "Eliminado",
              detail: "Documento eliminado exitosamente"
            });
          },
          (respuesta) => {
            toast.current?.show({
              severity: "error",
              summary: "Error",
              detail: respuesta[0]?.msg || "Error al eliminar el documento",
              sticky: true
            });
          }
        );
      }
    });
  }

  // Se busca en la lista en cada render para que el sidebar refleje los
  // cambios de estado que trae el polling.
  const documentoSeleccionado = documentos?.find(
    (d) => d.id === documentoSeleccionadoId
  );

  const menuItems = [
    {
      label: "Ver detalles",
      icon: "pi pi-eye",
      command: () => setSidebarDetalles(true),
    },
    // Reprocesar solo tiene sentido para documentos cuya extracción falló
    ...(documentoSeleccionado?.estado === "ERROR"
      ? [
          {
            label: "Reprocesar",
            icon: "pi pi-refresh",
            command: () => reprocesarDocumento(documentoSeleccionado),
          },
        ]
      : []),
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      command: () => eliminarDocumento(documentoSeleccionado),
    },
  ];

  function renderEstado(documento) {
    if (documento.estado === "PROCESANDO") {
      return (
        <Tag
          severity="warning"
          value="Procesando"
          icon="pi pi-spin pi-spinner"
        />
      );
    }
    if (documento.estado === "ERROR") {
      return (
        <span title={documento.detalleError || ""}>
          <Tag severity="danger" value="Error" icon="pi pi-times" />
        </span>
      );
    }
    return <Tag severity="success" value="Listo" icon="pi pi-check" />;
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={menuItems} popup ref={menuRef} />

      {!documentos && (
        <div
          css={{
            marginTop: "2em",
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem"
          }}
        >
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem"
            }}
          >
            <Skeleton width="15rem" height="2.5rem" />
            <Skeleton width="10rem" height="2.5rem" />
          </div>
          <Card>
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem"
              }}
            >
              <Skeleton width="100%" height="3rem" />
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} width="100%" height="3.5rem" />
              ))}
            </div>
          </Card>
        </div>
      )}

      {documentos && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <>
                {/* Input oculto: el Boton lo dispara, así el botón de subir
                    tiene el loader estándar sin cambiar su contenido */}
                <input
                  ref={inputArchivoRef}
                  type="file"
                  accept=".pdf,.txt,.md,.xlsx"
                  onChange={handleUploadArchivo}
                  css={{ display: "none" }}
                />
                <Boton
                  loader={subiendoArchivo}
                  disabled={subiendoArchivo}
                  callback={() => inputArchivoRef.current?.click()}
                >
                  Subir documento
                </Boton>
              </>
            }
          >
            Juno - Documentos del asistente
          </Titulo>

          <Card>
            <TablaFiltrable
              value={documentos}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="documentos_juno"
              size="small"
              rows={10}
              emptyMessage="No hay documentos cargados para el asistente"
            >
              <Column header="Documento" field="nombre" />
              <Column
                header="Estado"
                body={renderEstado}
                style={{ width: "9rem" }}
              />
              <Column
                body={(rowData) => (
                  <Boton
                    texto
                    negro
                    chico
                    callback={(e) => {
                      setDocumentoSeleccionadoId(rowData.id);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                )}
                style={{ width: "60px", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>

          <Sidebar
            visible={sidebarDetalles && !!documentoSeleccionado}
            position="right"
            onHide={() => setSidebarDetalles(false)}
            className="w-full lg:w-30rem md:w-30rem"
          >
            {documentoSeleccionado && (
              <>
                <Titulo>{documentoSeleccionado.nombre}</Titulo>
                <div className="grid">
                  <CampoInformacion nombreCampo="Resumen" anchoCompleto>
                    {documentoSeleccionado.resumen || "-"}
                  </CampoInformacion>
                  {documentoSeleccionado.estado === "ERROR" && (
                    <CampoInformacion nombreCampo="Error" anchoCompleto>
                      {documentoSeleccionado.detalleError || "-"}
                    </CampoInformacion>
                  )}
                </div>
              </>
            )}
          </Sidebar>
        </div>
      )}
    </>
  );
}
